import { create } from 'zustand';
import { FirestoreService, COLLECTIONS } from '../services/firestore';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../services/firebase';

export const useAssessmentStore = create((set, get) => ({
  // State
  assessments: [],
  currentAssessment: null,
  questions: [],
  isLoading: false,
  error: null,

  // Assessment CRUD operations
  createAssessment: async (assessmentData) => {
    try {
      set({ isLoading: true, error: null });
      
      const newAssessment = {
        ...assessmentData,
        questions: [],
        submissions: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.ASSESSMENTS), newAssessment);
      
      const createdAssessment = {
        id: docRef.id,
        ...newAssessment,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      set(state => ({
        assessments: [...state.assessments, createdAssessment],
        currentAssessment: createdAssessment,
        isLoading: false
      }));

      return createdAssessment;
    } catch (error) {
      console.error('Error creating assessment:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateAssessment: async (assessmentId, updates) => {
    try {
      set({ isLoading: true, error: null });
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, COLLECTIONS.ASSESSMENTS, assessmentId), updateData);

      set(state => ({
        assessments: state.assessments.map(assessment =>
          assessment.id === assessmentId
            ? { ...assessment, ...updates, updatedAt: new Date() }
            : assessment
        ),
        currentAssessment: state.currentAssessment?.id === assessmentId
          ? { ...state.currentAssessment, ...updates, updatedAt: new Date() }
          : state.currentAssessment,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error updating assessment:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  addQuestion: async (assessmentId, questionData) => {
    try {
      const questionId = `question_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newQuestion = {
        id: questionId,
        ...questionData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const assessment = get().currentAssessment;
      if (!assessment) throw new Error('No current assessment');

      const updatedQuestions = [...(assessment.questions || []), newQuestion];
      
      await get().updateAssessment(assessmentId, { questions: updatedQuestions });
      
      return newQuestion;
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  },

  updateQuestion: async (assessmentId, questionId, updates) => {
    try {
      const assessment = get().currentAssessment;
      if (!assessment) throw new Error('No current assessment');

      const updatedQuestions = assessment.questions.map(q =>
        q.id === questionId ? { ...q, ...updates, updatedAt: new Date() } : q
      );

      await get().updateAssessment(assessmentId, { questions: updatedQuestions });
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  },

  deleteQuestion: async (assessmentId, questionId) => {
    try {
      const assessment = get().currentAssessment;
      if (!assessment) throw new Error('No current assessment');

      const updatedQuestions = assessment.questions.filter(q => q.id !== questionId);
      await get().updateAssessment(assessmentId, { questions: updatedQuestions });
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  },

  // Load assessments
  loadAssessments: async (userId) => {
    try {
      set({ isLoading: true, error: null });
      
      const q = query(
        collection(db, COLLECTIONS.ASSESSMENTS),
        where('createdBy', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const assessments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      }));

      set({ assessments, isLoading: false });
      return assessments;
    } catch (error) {
      console.error('Error loading assessments:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Load single assessment
  loadAssessment: async (assessmentId) => {
    try {
      set({ isLoading: true, error: null });
      
      const docSnap = await getDoc(doc(db, COLLECTIONS.ASSESSMENTS, assessmentId));
      
      if (docSnap.exists()) {
        const assessment = {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate() || new Date(),
          updatedAt: docSnap.data().updatedAt?.toDate() || new Date()
        };
        
        set({ currentAssessment: assessment, isLoading: false });
        return assessment;
      } else {
        throw new Error('Assessment not found');
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Clear current assessment
  clearCurrentAssessment: () => {
    set({ currentAssessment: null });
  },

  // Publish assessment
  publishAssessment: async (assessmentId) => {
    try {
      await get().updateAssessment(assessmentId, { 
        status: 'published',
        publishedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error publishing assessment:', error);
      throw error;
    }
  }
}));